import { type Accessor, createSignal } from "solid-js";
import { errorMessage } from "../store/api-client";

/**
 * The busy/error pair a submit handler manages by hand: clear the error, flip
 * busy, run the task, turn a thrown cause into a message, clear busy again.
 * `fail` sets the error directly, for a caller's own validation or a catch
 * block that needs to run other code (a reload, a shared error signal) beside
 * reporting the message. `formatError` defaults to the shared `errorMessage`;
 * pass one only to keep an existing site's own fallback wording.
 */
type AsyncAction = {
  run: (task: () => Promise<void>) => Promise<void>;
  busy: Accessor<boolean>;
  error: Accessor<string>;
  fail: (message: string) => void;
};

export function createAsyncAction(
  formatError: (cause: unknown) => string = errorMessage,
): AsyncAction {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const run = async (task: () => Promise<void>): Promise<void> => {
    setError("");
    setBusy(true);
    try {
      await task();
    } catch (cause) {
      setError(formatError(cause));
    } finally {
      setBusy(false);
    }
  };

  return { run, busy, error, fail: setError };
}
